import React, { useState, useEffect, useCallback } from "react";
import { useForm, FormProvider } from "react-hook-form";
import TextController from "./TextController";
import AmountController from "./AmountController";
import SelectController from "./SelectController"; // Assuming you have a SelectController
import { Button } from "../ui/button";
import { TrashIcon } from "@radix-ui/react-icons";
import { PlusIcon } from "lucide-react";
import { Card, CardHeader, CardContent } from "../ui/card";
import { FormLabel } from "../ui/form";

const defaultItem: ItemType = {
  id: 0,
  name: "",
  frequency: "monthly",
  cost: 0,
  title: "",
};

const emptyCollection = { values: [defaultItem] };

interface ItemsComponentProps extends ItemFormProps {
  sectionTitle: string;
  sectionId: string;
}

const ItemsComponent: React.FC<ItemsComponentProps> = ({
  json,
  onSave,
  field,
  sectionTitle,
  sectionId,
}) => {
  const parseJsonSafely = (json: string | undefined): ItemType[] => {
    if (!json) return emptyCollection.values;
    const values = JSON.parse(json).values;
    return JSON.parse(values);
  };

  const methods = useForm();
  const { setValue } = methods;

  const [items, setItems] = useState<ItemType[]>(parseJsonSafely(json));

  useEffect(() => {
    const json = JSON.stringify(items);
    onSave(json, field);
  }, [items, onSave, field]);

  const handleAdd = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    const updatedItems = [
      ...items,
      { ...defaultItem, id: items.length, title: `Item ${items.length + 1}` },
    ];
    setItems(updatedItems);
  };

  const deleteItem = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, index: number) => {
      e.preventDefault();
      setItems((current) => {
        const newItems = [...current];
        newItems.splice(index, 1);
        return newItems;
      });
    },
    [setItems]
  );

  const handleInputChange = (
    index: number,
    field: keyof ItemType,
    value: string | number
  ) => {
    setItems((current) =>
      current.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    );
  };

  const handleSelectChange = (index: number, value: "monthly" | "annually") => {
    setItems((current) =>
      current.map((item, i) =>
        i === index ? { ...item, frequency: value } : item
      )
    );
  };

  return (
    <Card className="mb-8" id={sectionId} data-cy={sectionId}>
      <CardHeader>
        <h4 className="text-lg font-semibold text-gray-700">{sectionTitle}</h4>
      </CardHeader>
      <CardContent>
        <FormProvider {...methods}>
          <div>
            {items &&
              items.map((item, index) => (
                <div
                  key={item.id}
                  data-cy={`item-${item.id}-row`}
                  className="grid grid-cols-1 md:grid-cols-[2fr_2fr_2fr_auto] gap-x-8"
                >
                  <div className={`space-y-1 mb-1 item-${item.id}`}>
                    <TextController
                      // class={`item-${index}-name`}
                      name={`items[${index}].name`}
                      label="Name"
                      placeholder="Name"
                      defaultValue={item.name}
                      onChange={(value: any) =>
                        handleInputChange(index, "name", value)
                      }
                    />
                  </div>

                  <div
                    className={`space-y-1 mb-1 frequency-${item.id}`}
                    data-cy={`frequency-${item.id}`}
                  >
                    <SelectController
                      name={`items[${index}].frequency`}
                      label="Frequency"
                      defaultValue={item.frequency}
                      options={[
                        { value: "monthly", label: "Monthly" },
                        { value: "annually", label: "Annually" },
                      ]}
                      onChange={(value: any) =>
                        handleSelectChange(index, value)
                      }
                    />
                  </div>

                  <div
                    className={`space-y-1 mb-1 cost-${item.id}`}
                    data-cy={`cost-${item.id}`}
                  >
                    <AmountController
                      name={`items[${index}].cost`}
                      label="Cost"
                      placeholder="Cost"
                      defaultValue={item.cost}
                      onChange={(value: any) =>
                        handleInputChange(index, "cost", value)
                      }
                    />
                  </div>

                  <div className={`space-y-1 mb-1 delete-${item.id}`}>
                    <Button
                      variant="outline"
                      size="icon"
                      className="mt-8 delete-item"
                      data-cy={`delete-${item.id}`}
                      onClick={(e) => deleteItem(e, index)}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}

            <Button
              size="icon"
              className="mt-1 add-item"
              data-cy={`add-item-${sectionId}`}
              onClick={handleAdd}
            >
              <PlusIcon className="h-4 w-4" />
            </Button>
          </div>
        </FormProvider>
      </CardContent>
    </Card>
  );
};

export default ItemsComponent;
